import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, where, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface Invoice {
  id: string;
  invoiceNumber: string;
  fileUrl: string;
  driver: string;
  status: 'pending' | 'pay_to_driver';
  createdAt: any;
  updatedAt: any;
  orderNumber?: string;
  loadNumber?: string;
  broker?: string;
  terminal?: string;
  destination?: string;
  totalCharges?: number;
  [key: string]: any;
}

interface InvoiceState {
  items: Invoice[];
  loading: boolean;
  error: string | null;
}

const initialState: InvoiceState = {
  items: [],
  loading: false,
  error: null
};

// Fetch invoices based on user role
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async ({ isAdmin, dogtag }: { isAdmin: boolean; dogtag: string }) => {
    try {
      let q;
      if (isAdmin) {
        // Admin can see all invoices
        q = query(
          collection(db, 'invoices'),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Regular users can only see their own invoices
        q = query(
          collection(db, 'invoices'),
          where('driver', '==', dogtag),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const invoices = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('Fetched invoices:', invoices);
      return invoices as Invoice[];
    } catch (error: any) {
      console.error('Error fetching invoices:', error);
      throw new Error(error.message || 'Failed to fetch invoices');
    }
  }
);

// Update invoice status
export const updateInvoiceStatus = createAsyncThunk(
  'invoices/updateStatus',
  async ({ 
    invoiceId, 
    status 
  }: { 
    invoiceId: string; 
    status: 'pending' | 'pay_to_driver';
  }) => {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      await updateDoc(invoiceRef, { 
        status,
        updatedAt: new Date()
      });
      return { id: invoiceId, status };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update invoice status');
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setInvoices: (state, action) => {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch invoices';
      })
      // Update invoice status
      .addCase(updateInvoiceStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        const invoice = state.items.find(item => item.id === action.payload.id);
        if (invoice) {
          invoice.status = action.payload.status;
        }
        state.error = null;
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update invoice status';
      });
  }
});

export const { setInvoices } = invoiceSlice.actions;
export default invoiceSlice.reducer; 